import { type AuthOptions, type CampaignState, type Me, isPlayable } from '@chessbut/shared';
import { useSyncExternalStore } from 'react';
import { api } from './api.ts';
import { NATIVE } from './platform.ts';
import { load, save, storageKey, trySave } from './storage.ts';

export interface PublicStats {
  games: number;
  moves: number;
  players: number;
  accounts: number;
  countries: number;
  gamesToday: number;
  playersToday: number;
  peakOnline: number;
  day: number;
  variants: number;
  daily: { day: string; games: number; players: number }[];
  live: { online: number; playing: number; searching: Record<string, number> };
}

export interface SessionState {
  status: 'loading' | 'ready' | 'error';
  token: string | null;
  me: Me | null;
  campaign: CampaignState | null;
  stats: PublicStats | null;
  error: string | null;
}

interface SessionResponse {
  token: string;
  me: Me;
  campaign: CampaignState;
  stats: PublicStats;
}

let state: SessionState = { status: 'loading', token: load<string | null>('token', null), me: null, campaign: null, stats: null, error: null };
const listeners = new Set<() => void>();
const tokenListeners = new Set<() => void>();

/**
 * Bumped whenever the token changes or the account behind it does (a guest claimed by email
 * keeps its token), so a session reply from before a sign-in/out is ignored.
 */
let generation = 0;

/** Whether local storage keeps the token (private modes can refuse); if not, only `generation` guards. */
const storageWorks = trySave('storage-probe', 1);

function set(patch: Partial<SessionState>) {
  const tokenChanged = patch.token !== undefined && patch.token !== state.token;
  if (tokenChanged) generation++;
  state = { ...state, ...patch };
  for (const l of listeners) l();
  if (tokenChanged) for (const l of tokenListeners) l();
}

export const getToken = () => state.token;
export const getSession = () => state;

const switchListeners = new Set<(from: string, to: string) => void>();

/** Called when this device signs in as another player (a guest's things now belong to the account). */
export function onPlayerSwitch(l: (from: string, to: string) => void) {
  switchListeners.add(l);
  return () => switchListeners.delete(l);
}

/** Called when the session token changes (login/logout), e.g. to reconnect the socket. */
export function onTokenChange(l: () => void) {
  tokenListeners.add(l);
  return () => tokenListeners.delete(l);
}

/** Where this visit came from: ?ref= / utm_source on the link, else the referrer. */
function takeRef(): { ref: string | null; referrer: string | null } {
  const url = new URL(location.href);
  const ref = url.searchParams.get('ref') ?? url.searchParams.get('utm_source');
  const campaign = url.searchParams.get('utm_campaign');
  let changed = false;
  for (const k of ['ref', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'fbclid', 'igshid']) {
    if (url.searchParams.has(k)) {
      url.searchParams.delete(k);
      changed = true;
    }
  }
  if (changed) history.replaceState(history.state, '', url.pathname + url.search + url.hash);
  return { ref: ref ? (campaign ? `${ref}-${campaign}` : ref) : null, referrer: document.referrer || null };
}

let started = false;

/** The last good session, so the app still opens (and bot games work) without a connection. */
interface SessionCache {
  token: string;
  me: Me;
  campaign: CampaignState;
  stats: PublicStats;
}

/**
 * A session reply, unless a sign-in or sign-out (in this tab or another) happened while it was
 * on its way: then it belongs to the old session and must not overwrite the new one.
 */
function applySession(res: SessionResponse, sentToken: string | null, gen: number): boolean {
  if (gen !== generation) return false;
  if (storageWorks && load<string | null>('token', null) !== sentToken) return false;
  save('token', res.token);
  save('session-cache', { token: res.token, me: res.me, campaign: res.campaign, stats: res.stats } satisfies SessionCache);
  set({ status: 'ready', token: res.token, me: res.me, campaign: res.campaign, stats: res.stats, error: null });
  return true;
}

let retryTimer: ReturnType<typeof setInterval> | null = null;

/** Offline: carry on with the last session (or none) and keep trying in the background. */
function goOffline(message: string) {
  const cache = load<SessionCache | null>('session-cache', null);
  if (cache && cache.token === state.token) set({ status: 'ready', me: cache.me, campaign: cache.campaign, stats: cache.stats, error: 'offline' });
  else set({ status: 'error', error: message });
  if (retryTimer) return;
  const retry = () => {
    if (state.error === null) {
      if (retryTimer) clearInterval(retryTimer);
      retryTimer = null;
      return;
    }
    void refreshSession();
  };
  retryTimer = setInterval(retry, 20_000);
  window.addEventListener?.('online', retry);
}

/** Starts (or resumes) the session: creates a guest on first visit. */
export async function initSession() {
  if (started) return;
  started = true;
  // Signing in or out in another tab switches this one too.
  window.addEventListener?.('storage', (e) => {
    if (e.key !== storageKey('token')) return;
    const token = e.newValue ? (JSON.parse(e.newValue) as string) : null;
    if (token === state.token) return;
    if (token) {
      set({ token });
      void refreshSession();
    } else {
      // Signed out in another tab: drop this account's details here too. That tab starts the
      // new guest session; its token arrives as another storage event.
      generation++;
      set({ token: null, me: null, campaign: null });
    }
  });
  const { ref, referrer } = takeRef();
  for (let attempt = 0; ; attempt++) {
    const sent = state.token;
    const gen = generation;
    try {
      const res = await api<SessionResponse>('/api/session', { body: { token: sent, ref, referrer, path: location.pathname }, token: null });
      if (!applySession(res, sent, gen)) await refreshSession();
      return;
    } catch (err) {
      if (attempt >= 3) {
        goOffline((err as Error).message);
        return;
      }
      await new Promise((r) => setTimeout(r, 800 * 2 ** attempt));
    }
  }
}

/** Re-fetches the session (campaign, stats, me). Also recovers from an expired token. */
export async function refreshSession() {
  // Another tab may have signed in since this one loaded: use the newest token.
  const stored = load<string | null>('token', null);
  if (stored && stored !== state.token) set({ token: stored });
  const sent = state.token;
  const gen = generation;
  try {
    const res = await api<SessionResponse>('/api/session', { body: { token: sent }, token: null });
    applySession(res, sent, gen);
  } catch {
    // keep what we have
  }
}

export function setMe(me: Me) {
  set({ me });
}

export interface SignInResult {
  token: string | null;
  me: Me;
  isNew: boolean;
  /** This device's guest was folded into the account. */
  merged: boolean;
}

/** A finished sign-in: a token means this device switched to that account. */
function applySignIn(res: SignInResult): SignInResult {
  generation++; // even a claim that keeps the token changes who it belongs to
  const before = state.me?.id;
  if (res.token) {
    save('token', res.token);
    set({ token: res.token, me: res.me });
  } else set({ me: res.me });
  // Only a guest folded into the account hands its things over; switching between existing
  // accounts doesn't.
  if (res.merged && before && before !== res.me.id) for (const l of switchListeners) l(before, res.me.id);
  void refreshSession(); // the campaign can differ for admins
  return res;
}

let options: Promise<AuthOptions> | null = null;
export function authOptions(): Promise<AuthOptions> {
  options ??= api<AuthOptions>('/api/auth/options')
    // The web redirect flows for Google/Apple don't work inside the app's web view.
    .then((o) => (NATIVE ? { ...o, google: false, apple: false } : o))
    .catch(() => {
      options = null;
      return { email: false, password: true, google: false, apple: false };
    });
  return options;
}

export async function signup(username: string, password: string) {
  const res = await api<{ me: Me }>('/api/auth/signup', { body: { username, password } });
  set({ me: res.me });
  return res.me;
}

export async function login(username: string, password: string) {
  return applySignIn(await api<SignInResult>('/api/auth/login', { body: { username, password } })).me;
}

/** Emails a 6-digit code (and a link). `attach` adds the email to the signed-in account. */
export function startEmail(email: string, purpose: 'login' | 'attach' = 'login') {
  return api<{ id: string; sentTo: string }>('/api/auth/email/start', { body: { email, purpose } });
}

export async function verifyEmailCode(id: string, code: string) {
  return applySignIn(await api<SignInResult>('/api/auth/email/verify', { body: { id, code } }));
}

export async function verifyEmailLink(link: string) {
  return applySignIn(await api<SignInResult>('/api/auth/email/link', { body: { link } }));
}

/** Leaves for Google / Apple; they send the browser back to /signin. */
export async function startOAuth(provider: 'google' | 'apple', returnTo = location.pathname) {
  const { url } = await api<{ url: string }>(`/api/auth/oauth/${provider}/start`, { body: { return: returnTo } });
  location.href = url;
}

export async function finishOAuth(handoff: string) {
  return applySignIn(await api<SignInResult>('/api/auth/oauth/finish', { body: { handoff } }));
}

export async function changeUsername(username: string) {
  const res = await api<{ me: Me }>('/api/me/username', { body: { username } });
  generation++;
  set({ me: res.me });
  return res.me;
}

export async function setPassword(next: string, current?: string) {
  await api('/api/me/password', { body: { next, current } });
  await reloadMe();
}

export async function logout() {
  // Drop the token first so the socket closes on our side before the server revokes it
  // (otherwise its "unauthorized" would start a second guest session).
  const token = state.token;
  save('token', null);
  save('session-cache', null);
  // The campaign may list an admin's drafts; drop it with the account.
  set({ token: null, me: null, campaign: null });
  try {
    await api('/api/auth/logout', { body: {}, token });
  } catch {
    // ignore
  }
  started = false;
  await initSession();
}

export async function reloadMe() {
  try {
    const res = await api<{ me: Me }>('/api/me');
    set({ me: res.me });
  } catch {
    // ignore
  }
}

/**
 * Whether a variant can be opened: released for everyone, or a draft/future day that the
 * server listed for the signed-in admin. Without a campaign yet, only the public schedule.
 */
export function canOpenVariant(session: Pick<SessionState, 'campaign' | 'me'>, id: string): boolean {
  const { campaign, me } = session;
  if (!campaign) return isPlayable(id);
  const entry = campaign.released.find((r) => r.variant === id);
  if (!entry) return false;
  return !!me?.admin || (!entry.draft && entry.day <= campaign.dayNumber);
}

export function useSession(): SessionState {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => state,
  );
}
